/**
 * Subdomain → tenant resolution per arch §A.7 / UX spec §3.3.
 *
 * - {tenant-slug}.elreign.io  → tenantSlug = 'tenant-slug'
 * - app.elreign.io            → tenantSlug = null  (apex app — user picks tenant after sign-in)
 * - elreign.io                → tenantSlug = null  (marketing)
 * - localhost / 127.0.0.1     → tenantSlug = null  (dev; override via ?tenant=slug for local testing)
 *
 * Tenant context is then resolved by calling /api/v1/branding/me with the
 * tenantSlug header (or by reading the JWT's tenantId claim once the user is
 * signed in). 9.1 only resolves the SLUG; the API call is a 9.4 deliverable.
 *
 * Reserved subdomains that are NOT tenant slugs:
 *   app, app-dev, app-staging, app-prod, www, api, cdn, mail, smtp, status,
 *   support, admin-elreign
 *
 * The `app-{env}` entries are the per-environment apex app hosts (the dev app
 * is served at `app-dev.elreign.io`). Without reserving them they'd resolve as
 * a tenant slug and fire a guaranteed-404 `branding/by-slug/app-dev` request on
 * every page load (BUG 5).
 *
 * Tenant slug validation: lowercase alphanumeric + hyphens, 2–63 chars.
 * Matches what the backend accepts when provisioning a tenant subdomain.
 */

const RESERVED_SUBDOMAINS = new Set([
  'app',
  'app-dev',
  'app-staging',
  'app-prod',
  'www',
  'api',
  'cdn',
  'mail',
  'smtp',
  'status',
  'support',
  'admin-elreign',
]);

const ROOT_DOMAIN = 'elreign.io';

/**
 * The host a firm's clients actually reach it on.
 *
 * Two bugs this closes, both found walking a real branded firm on 2026-08-11:
 *  - the admin dashboard hard-coded `app.elreign.io`, so a firm sitting on
 *    destinata.elreign.io was told its portal was the generic Elreign address;
 *  - the sidebar printed the bare slug directly under the firm name, which for
 *    a one-word firm is the same word twice ("Destinata" over "destinata").
 *
 * Rendering the host instead of the slug fixes both: it is not a repeat of the
 * name, and it is the thing a firm actually wants to see and share.
 */
export function portalHostForTenant(
  slug?: string | null,
  customDomain?: string | null,
): string {
  const domain = (customDomain ?? '').trim();
  if (domain) return domain;
  const s = (slug ?? '').trim().toLowerCase();
  // A tenant UUID passes SLUG_RE (lowercase hex + hyphens, under 63 chars), and
  // callers fall back to the tenant id when the slug is missing — which is how
  // you end up advertising "8c76d84e-45f1-….elreign.io" to a firm. The
  // dashboard-hero-wiring test flagged this exact hazard ("the slug used to be
  // the tenant UUID besides"), so reject that shape explicitly.
  const isTenantUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(s);
  if (s && !isTenantUuid && SLUG_RE.test(s) && !RESERVED_SUBDOMAINS.has(s)) {
    return `${s}.${ROOT_DOMAIN}`;
  }
  return `app.${ROOT_DOMAIN}`;
}

/** Regex for a valid tenant slug: 2–63 lowercase alnum + hyphens, no leading/trailing hyphen. */
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$|^[a-z0-9]$/;

/**
 * Resolves the tenant slug from the current browser host string.
 *
 * @param host    - `window.location.host` (includes port; e.g. "iprotect.elreign.io" or "localhost:5173")
 * @param search  - URLSearchParams from the current URL (for ?tenant= dev override)
 * @returns       tenant slug string, or null for apex / marketing / local dev
 */
export function resolveTenantSlugFromHost(
  host: string,
  search?: URLSearchParams,
): string | null {
  // Strip port from host for subdomain extraction.
  const hostname = host.split(':')[0] ?? '';

  if (!hostname) return null;

  // Localhost / loopback — no subdomain; allow ?tenant= override for local dev.
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
    const tenantOverride = search?.get('tenant');
    if (tenantOverride && SLUG_RE.test(tenantOverride) && !RESERVED_SUBDOMAINS.has(tenantOverride)) {
      return tenantOverride;
    }
    return null;
  }

  // Must end with .elreign.io to be a valid tenant subdomain.
  if (!hostname.endsWith(`.${ROOT_DOMAIN}`)) {
    // Could be an arbitrary custom domain — 9.4 handles that case via API.
    return null;
  }

  // Extract subdomain: remove the ".elreign.io" suffix.
  const subdomain = hostname.slice(0, hostname.length - ROOT_DOMAIN.length - 1);

  // No subdomain (bare elreign.io).
  if (!subdomain) return null;

  // Nested subdomains (e.g. foo.bar.elreign.io) are not valid tenant slugs.
  if (subdomain.includes('.')) return null;

  // Reserved subdomain (app, www, api, …).
  if (RESERVED_SUBDOMAINS.has(subdomain)) return null;

  // Validate slug format.
  if (!SLUG_RE.test(subdomain)) return null;

  return subdomain;
}
